import React, { useEffect, useState } from "react";
import { OutlinedButton, PrimaryButton } from "@bloomberg/media-ui-core";
import get from "lodash.get";
import type { Position } from "@foundation/watchlist-client";
import DataTable from "./DataTable";
import styles from "./styles.module.css";
import {
  PortfolioColumnConfig,
  PortfolioLot,
  PortfolioSortState,
} from "~types/portfolio";
import RemainingSymbolsCount from "~components/Portfolio/RemainingSymbolsCount";
import {
  PORTFOLIO_TABLE_COLUMNS_CONFIG,
  SORT_STATES,
} from "~constants/Portfolio";
import WatchlistFileTransfer from "~components/WatchlistFileTransfer";

const DECREASING = SORT_STATES.DECREASING;

type PortfolioTableProps = {
  positions: Array<Position>;
  shouldDisplayLots: boolean;
  cycleSortState: (column: string) => void;
  sortState: PortfolioSortState;
  onToggleLotsClick: () => void;
  title: string;
  onEditClick: () => void;
};

export function flattenTickerAndLots(
  tickers: Array<Position>,
  shouldDisplayLots: boolean,
  sortState: PortfolioSortState
) {
  const { columnName, direction } = sortState;
  const columnConfig = PORTFOLIO_TABLE_COLUMNS_CONFIG.find(
    (config) => config.columnName === columnName
  ) as PortfolioColumnConfig;
  const rows: Array<Position | PortfolioLot> = [];

  // [DS] Currently this mutates the tickers in the redux store as a side effect, and thus persists
  // in the backend. This behavior is desired, but the code needs to be refactored in the long term
  // and be put in the reducer, but can't due to too many required code changes at the moment.
  tickers
    .sort((pos1, pos2) => {
      const val1 = get(pos1, columnConfig.tickerPrimaryLodashPath, null);
      const val2 = get(pos2, columnConfig.tickerPrimaryLodashPath, null);

      return sortPositions(val1, val2, direction);
    })
    .forEach((ticker) => {
      const { lots } = ticker;
      const securityType = ticker.security.SECTYPE;

      rows.push(ticker);

      if (shouldDisplayLots) {
        lots.forEach((lot) => {
          rows.push({ ...lot, securityType });
        });
      }
    });

  return rows;
}

function sortPositions(
  val1: string | number | null,
  val2: string | number | null,
  direction: string
) {
  // Sorting condition below ported from horseshoe
  if (val1 === null && val2 === null) {
    return 0;
  }

  if (val2 === null) {
    return -1;
  }

  if (val1 === null) {
    return 1;
  }

  if (direction === DECREASING) {
    return val2 > val1 ? 1 : -1;
  }

  return val1 > val2 ? 1 : -1;
}

export default function PortfolioTable(props: PortfolioTableProps) {
  const {
    positions,
    shouldDisplayLots,
    sortState,
    cycleSortState,
    onToggleLotsClick,
    onEditClick,
    title,
  } = props;
  const [watchlistRows, setWatchlistRows] = useState<
    Array<Position | PortfolioLot>
  >([]);

  useEffect(() => {
    const flattenRows = flattenTickerAndLots(
      positions,
      shouldDisplayLots,
      sortState
    );

    setWatchlistRows(flattenRows);
  }, [shouldDisplayLots, sortState.columnName, sortState.direction, positions]);

  const columnsConfig = shouldDisplayLots
    ? PORTFOLIO_TABLE_COLUMNS_CONFIG
    : PORTFOLIO_TABLE_COLUMNS_CONFIG.filter(
        (config) => config.columnName !== "LOTS"
      );

  return (
    <div className={styles.watchlistTable}>
      <div className={styles.tableActions}>
        <WatchlistFileTransfer type="export" />

        <div className={styles.buttons}>
          <OutlinedButton
            onClick={onToggleLotsClick}
            title={title}
            version="phoenix"
            fullWidth
            className={styles.button}
          >
            {title}
          </OutlinedButton>
          <PrimaryButton
            onClick={onEditClick}
            title="Edit watchlist"
            version="phoenix"
            fullWidth
            className={styles.button}
          >
            Edit
          </PrimaryButton>
        </div>
      </div>
      <DataTable
        watchlistRows={watchlistRows}
        columnsConfig={columnsConfig}
        sortState={sortState}
        cycleSortState={cycleSortState}
      />
      <div className={styles.remainingSymbolsWrapper}>
        <RemainingSymbolsCount holdingsCount={positions.length} />
      </div>
    </div>
  );
}
